import { useState } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import axios from "axios";
import { AlertCircle, ArrowRight, ShieldCheck } from "lucide-react";
import { API_BASE, TOKEN_KEY } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

export default function AdminLogin() {
  const { user, setSession } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  // If already logged in as super admin, redirect.
  if (user?.is_super_admin) return <Navigate to="/admin" replace />;

  async function onSubmit(e) {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      const { data } = await axios.post(`${API_BASE}/auth/login`, {
        email: email.trim(),
        password,
      });
      if (!data?.user?.is_super_admin) {
        setError("This login is for platform admins only. Regular tenants must use /login.");
        setBusy(false);
        return;
      }
      localStorage.setItem(TOKEN_KEY, data.access_token);
      await setSession(data.access_token, data.user);
      nav("/admin", { replace: true });
    } catch (err) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;
      if (status === 401 || status === 403) {
        setError(typeof detail === "string" ? detail : "Invalid credentials");
      } else {
        setError(err?.message || "Unable to sign in");
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <div
      className="flex min-h-screen items-center justify-center bg-[#0b1220] p-6 text-slate-100"
      data-testid="admin-login-page"
    >
      <div className="w-full max-w-md">
        <div className="mb-8 flex flex-col items-center gap-3">
          <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-amber-500 text-slate-950 shadow-lg shadow-amber-500/30">
            <ShieldCheck className="h-7 w-7" />
          </div>
          <div className="text-center">
            <h1 className="text-2xl font-semibold tracking-tight">Platform Admin</h1>
            <p className="mt-1 text-sm text-slate-400">
              Control plane for InvoicePro. Not a tenant account.
            </p>
          </div>
        </div>

        <form
          onSubmit={onSubmit}
          className="space-y-4 rounded-2xl border border-slate-800 bg-slate-900/70 p-6 shadow-xl backdrop-blur"
          data-testid="admin-login-form"
        >
          <div className="space-y-1.5">
            <label className="text-xs font-semibold uppercase tracking-wide text-slate-400" htmlFor="admin-email">
              Email
            </label>
            <input
              id="admin-email"
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              data-testid="admin-login-email"
              className="w-full rounded-lg border border-slate-700 bg-slate-950 px-3 py-2.5 text-sm text-slate-100 placeholder:text-slate-500 focus:border-amber-500 focus:outline-none focus:ring-2 focus:ring-amber-500/30"
              placeholder="you@nordicscandiskroyal.se"
            />
          </div>

          <div className="space-y-1.5">
            <label className="text-xs font-semibold uppercase tracking-wide text-slate-400" htmlFor="admin-password">
              Password
            </label>
            <input
              id="admin-password"
              type="password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              data-testid="admin-login-password"
              className="w-full rounded-lg border border-slate-700 bg-slate-950 px-3 py-2.5 text-sm text-slate-100 placeholder:text-slate-500 focus:border-amber-500 focus:outline-none focus:ring-2 focus:ring-amber-500/30"
            />
          </div>

          {error && (
            <div
              className="flex items-start gap-2 rounded-lg border border-red-500/30 bg-red-500/10 px-3 py-2 text-sm text-red-200"
              data-testid="admin-login-error"
              role="alert"
            >
              <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
              <span>{error}</span>
            </div>
          )}

          <button
            type="submit"
            disabled={busy}
            data-testid="admin-login-submit"
            className="flex w-full items-center justify-center gap-2 rounded-lg bg-amber-500 px-4 py-2.5 text-sm font-semibold text-slate-950 shadow-md shadow-amber-500/20 hover:bg-amber-400 disabled:opacity-60"
          >
            {busy ? "Signing in..." : "Sign in"}
            <ArrowRight className="h-4 w-4" />
          </button>

          <div className="text-center text-xs text-slate-500">
            Looking for the tenant app?{" "}
            <Link to="/login" className="text-amber-400 hover:underline">
              Go to /login
            </Link>
          </div>
        </form>
      </div>
    </div>
  );
}
